import { Skeleton } from '@mui/material';
import { forwardRef, lazy, Suspense } from 'react';

import type { InputEditorHandle, InputEditorProps } from './input-editor.types';

// The editor is built on Slate, which is large and only a handful of forms
// use. Loading it on demand keeps it out of the bundle every page downloads —
// this package is imported everywhere through its barrel, so a plain export
// here would ship the editor to all of them.
const LazyInputEditor = lazy(() =>
  import('./input-editor').then((m) => ({ default: m.InputEditor })),
);

// The editor's own measurements, so the placeholder is the height it will be
// and the form below doesn't move when it loads. The toolbar is one row of
// small icon buttons (30) in 8px of padding with a 1px divider; `minHeight`
// already includes the content area's padding (box-sizing is border-box).
const TOOLBAR_HEIGHT = 47;
const BORDER_HEIGHT = 2;
const DEFAULT_MIN_HEIGHT = 150;

// The ref is passed through to the editor: it is null until the chunk loads.
export const InputEditor = forwardRef<InputEditorHandle, InputEditorProps>(
  (props, ref) => (
    <Suspense
      fallback={
        <Skeleton
          variant="rounded"
          height={
            (props.minHeight ?? DEFAULT_MIN_HEIGHT) +
            BORDER_HEIGHT +
            // A disabled editor has no toolbar either.
            (props.showToolbar === false || props.disabled ? 0 : TOOLBAR_HEIGHT)
          }
        />
      }
    >
      <LazyInputEditor {...props} ref={ref} />
    </Suspense>
  ),
);
InputEditor.displayName = 'InputEditor';

export * from './input-editor.types';
